import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Building2, Check, ChevronDown, CircleHelp, Keyboard, LogOut, Menu, Moon, Search, Settings, Sun, UserRound } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { navFor } from './nav.js';
import { useState } from 'react';
import { api } from '../api/instance.js';
import { useSession } from '../lib/session.js';
import { notifyError } from '../lib/toast.js';
import { areaLabel } from '../lib/plain.js';
import { isDark, toggleTheme } from '../lib/theme.js';
import { Button } from '../components/ui/button.js';
import { Avatar, Kbd } from '../components/ui/misc.js';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu.js';
import { Breadcrumbs } from './Breadcrumbs.js';
import { OnboardingHelpItems } from '../onboarding/index.js';
import { cn } from '../lib/utils.js';

export function Topbar({ onMenu, onSearch }: { onMenu: () => void; onSearch: () => void }) {
  return (
    <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b bg-card/85 px-3 backdrop-blur supports-[backdrop-filter]:bg-card/70 sm:px-5">
      <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open menu" onClick={onMenu}><Menu /></Button>
      <AreaSwitcher />
      <div className="mx-1 hidden h-5 w-px bg-border md:block" aria-hidden />
      <Breadcrumbs />
      <div className="ml-auto flex items-center gap-1.5">
        <button type="button" onClick={onSearch}
          className="hidden h-9 w-56 items-center lg:w-72 gap-2 rounded-md border bg-background px-3 text-sm text-muted-foreground shadow-sm hover:bg-muted md:flex">
          <Search className="h-4 w-4" aria-hidden /> Search pages and people…
          <span className="ml-auto flex gap-0.5"><Kbd>⌘</Kbd><Kbd>K</Kbd></span>
        </button>
        <Button variant="ghost" size="icon" className="md:hidden" aria-label="Search" onClick={onSearch}><Search /></Button>
        <ThemeButton />
        <HelpMenu onSearch={onSearch} />
        <Notifications />
        <UserMenu />
      </div>
    </header>
  );
}

function AreaSwitcher() {
  const { me, vertical, setVertical } = useSession();
  const verticals = useQuery({ queryKey: ['verticals'], queryFn: () => api.call('listVerticals') });
  const mine = new Set(me?.memberships.map((m) => m.verticalKey));
  const items = (verticals.data?.items ?? []).filter((v) => mine.has(v.key));
  const current = vertical === 'all' ? 'All business areas' : items.find((v) => v.key === vertical)?.name ?? areaLabel(vertical);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 max-w-[52vw] gap-2" aria-label="Business area">
          <Building2 className="text-muted-foreground" />
          <span className="truncate">{current}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60">
        <DropdownMenuLabel>Business area</DropdownMenuLabel>
        {verticals.isError && <p className="px-2.5 py-1.5 text-xs text-danger">Couldn't load business areas.</p>}
        {[{ key: 'all', name: 'All business areas' }, ...items].map((v) => (
          <DropdownMenuItem key={v.key} onSelect={() => setVertical(v.key)}>
            <Check className={cn(vertical === v.key ? 'opacity-100' : 'opacity-0')} />
            {v.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Notifications() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.call('listNotifications', { query: { limit: 20 } }),
    refetchInterval: 60_000,
  });
  const read = useMutation({
    mutationFn: (id: string) => api.call('markNotificationRead', { params: { id } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
    onError: notifyError,
  });
  const items = q.data?.items ?? [];
  const unread = items.filter((n) => !n.readAt).length;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'} className="relative">
          <Bell />
          {unread > 0 && (
            <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold text-danger-foreground">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[22rem] p-0">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <p className="text-sm font-semibold">Notifications</p>
          {unread > 0 && <span className="text-xs text-muted-foreground">{unread} unread</span>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {q.isPending && <p className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</p>}
          {q.isError && <p className="px-4 py-6 text-center text-sm text-danger">Couldn't load notifications. Try again.</p>}
          {q.isSuccess && items.length === 0 && <p className="px-4 py-8 text-center text-sm text-muted-foreground">You're all caught up.</p>}
          {items.map((n) => (
            <DropdownMenuItem key={n.id} className="items-start gap-3 rounded-none border-b px-4 py-3 last:border-0"
              onSelect={() => {
                if (!n.readAt) read.mutate(n.id);
                if (n.relationshipId) navigate(`/pipeline/${n.relationshipId}`);
              }}>
              <span className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', n.readAt ? 'bg-transparent' : 'bg-primary')} aria-hidden />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-foreground">{n.title}</span>
                <span className="line-clamp-2 block text-xs text-muted-foreground">{n.body}</span>
                <span className="mt-0.5 block text-[11px] text-muted-foreground">
                  {new Date(n.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </span>
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ThemeButton() {
  const [dark, setDark] = useState(isDark);
  return (
    <Button variant="ghost" size="icon" className="hidden sm:inline-flex" aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'} title={dark ? 'Light mode' : 'Dark mode'}
      onClick={() => { toggleTheme(); setDark(isDark()); }}>
      {dark ? <Sun /> : <Moon />}
    </Button>
  );
}

/** "?" in the top bar: what this page is for, and how to get around. */
function HelpMenu({ onSearch }: { onSearch: () => void }) {
  const { pathname } = useLocation();
  const hit = navFor(pathname);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Help" data-tour="help-menu"><CircleHelp /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="px-3 py-2.5">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">About this page</p>
          <p className="mt-1 text-sm font-semibold">{hit?.item.label ?? 'Business Sense'}</p>
          <p className="mt-0.5 text-sm text-muted-foreground">{hit?.item.description ?? 'Your sales work, in one place.'}</p>
        </div>
        <DropdownMenuSeparator />
        <div className="px-3 py-2 text-sm text-muted-foreground">
          Tap any small <CircleHelp className="inline h-3.5 w-3.5 align-[-2px]" aria-hidden /> next to a title, column or field for a plain explanation with an example.
        </div>
        <DropdownMenuItem onSelect={onSearch}><Keyboard /> Search pages and people <span className="ml-auto flex gap-0.5"><Kbd>⌘</Kbd><Kbd>K</Kbd></span></DropdownMenuItem>
        <OnboardingHelpItems />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu() {
  const { me, signOut } = useSession();
  const navigate = useNavigate();
  const [dark, setDark] = useState(isDark);
  if (!me) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label="Your account" className="ml-1 flex items-center gap-2 rounded-full py-0.5 pl-0.5 pr-1 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:rounded-md md:pr-2">
          <Avatar name={me.name} />
          <span className="hidden text-left leading-tight md:block">
            <span className="block max-w-[9rem] truncate text-sm font-medium">{me.name}</span>
            {/* No roles (owner decision 2026-09-21): no Admin/Member label. */}
          </span>
          <ChevronDown className="hidden h-4 w-4 text-muted-foreground md:block" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60">
        <div className="px-2.5 py-2">
          <p className="truncate text-sm font-semibold">{me.name}</p>
          <p className="truncate text-xs text-muted-foreground">{me.email}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/settings')}><UserRound /> Profile</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/settings')}><Settings /> Settings</DropdownMenuItem>
        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); toggleTheme(); setDark(isDark()); }}>
          {dark ? <Sun /> : <Moon />} {dark ? 'Light mode' : 'Dark mode'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onSelect={() => { signOut().catch(notifyError); }}><LogOut /> Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
